import React, { useMemo } from "react";
import { COMPANY as COMPANY_DEFAULT, CONTACT_EMAIL, CONTACT_PHONE } from "../../config/company";
import { LEGAL_NAV, LEGAL_VERSION, LEGAL_JURISDICTION } from "./legalContent";
import "./TermsPage.css";

function LegalList({ items }) {
  return (
    <ul className="cgu-legal-list">
      {items.map(([label, value]) => (
        <li key={label}>
          <strong>{label} :</strong> {value}
        </li>
      ))}
    </ul>
  );
}

export default function TermsPage({
  company = {
    ...COMPANY_DEFAULT,
    rcs: `RCS Paris ${COMPANY_DEFAULT.siren.replace(/\s/g, "")}`,
  },
  effectiveDate = LEGAL_VERSION,
  jurisdiction = LEGAL_JURISDICTION,
}) {
  const siteName = useMemo(() => company?.name || "ProMeetService", [company]);
  const mail = company.email || CONTACT_EMAIL;
  const tel = company.phone || CONTACT_PHONE;
  const telHref = company.phoneTel || "";

  const mentionsItems = [
    ["Raison sociale", `${siteName} — ${company.legalForm}`],
    ["SIREN", company.siren],
    ["SIRET (siège social)", company.siret],
    ["N° TVA intracommunautaire", company.tva],
    ["Siège social", company.address],
    ["Représentant légal", company.director],
    [
      "Contact",
      <>
        <a href={`mailto:${mail}`}>{mail}</a>
        {tel ? (
          <>
            {" "}
            — <a href={`tel:${telHref}`}>{tel}</a>
          </>
        ) : null}
      </>,
    ],
    ["Hébergeur", `${company.hostName}, ${company.hostAddress}`],
  ];

  return (
    <div className="cgu-wrap">
      <header className="cgu-header">
        <h1>Informations légales ProMeetService</h1>
        <p className="cgu-lead">
          Mentions légales, conditions d&apos;utilisation, conditions de vente, protection des
          données et politique cookies applicables au site et à l&apos;application mobile.
        </p>
        <div className="cgu-meta">
          <span>Version en vigueur : {effectiveDate}</span>
          <button type="button" className="cgu-print" onClick={() => window.print()}>
            Imprimer / PDF
          </button>
        </div>
        <nav className="cgu-nav" aria-label="Sommaire légal">
          {LEGAL_NAV.map((item) => (
            <a key={item.id} href={`#${item.id}`} className="cgu-nav__link">
              {item.label}
            </a>
          ))}
        </nav>
      </header>

      {/* ——— Mentions légales ——— */}
      <section className="cgu-card" id="mentions-legales">
        <h2>Mentions légales</h2>
        <LegalList items={mentionsItems} />
        <p>
          Conformément à la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l&apos;économie
          numérique (LCEN), les informations ci-dessus permettent d&apos;identifier l&apos;éditeur du
          service et de le contacter.
        </p>
      </section>

      {/* ——— CGU ——— */}
      <section className="cgu-card" id="cgu">
        <h2>Conditions générales d&apos;utilisation (CGU)</h2>

        <h3>1. Objet de la Plateforme</h3>
        <p>
          <b>{siteName}</b> est une plateforme d&apos;intermédiation et d&apos;outils numériques
          permettant notamment :
        </p>
        <ul>
          <li>
            aux <b>Clients</b> de rechercher des professionnels, consulter leurs disponibilités et
            réserver des rendez-vous ;
          </li>
          <li>
            aux <b>Professionnels (« Pros »)</b> de publier une annonce vérifiée, gérer un ou
            plusieurs profils d&apos;activité, un agenda, des documents (devis, factures) et des
            abonnements ;
          </li>
          <li>
            aux <b>ProMeet Partners</b> (prestataires partenaires validés) de postuler à des missions
            du module <b>ProMeet Missions</b>, selon leurs spécialités approuvées par
            l&apos;administration ;
          </li>
          <li>
            aux professionnels habilités de <b>publier des missions B2B</b> (secteur automobile :
            carrosserie, jantes, convoyage, nettoyage, etc.) soumises à validation administrative.
          </li>
        </ul>
        <p>
          Sauf stipulation contraire, {siteName} agit en qualité d&apos;<b>intermédiaire technique</b>{" "}
          et n&apos;est pas partie au contrat de prestation conclu entre un Client et un Pro, ni
          entre les acteurs de ProMeet Missions.
        </p>

        <h3>2. Acceptation</h3>
        <p>
          La création d&apos;un compte, la navigation ou l&apos;utilisation de la Plateforme
          implique l&apos;acceptation pleine et entière des présentes CGU, de la politique de
          confidentialité et de la politique cookies. L&apos;utilisateur est invité à en
          conserver une copie.
        </p>

        <h3>3. Comptes et éligibilité</h3>
        <h4>3.1 Clients</h4>
        <ul>
          <li>Être majeur et juridiquement capable, ou dûment représenté.</li>
          <li>Fournir des coordonnées exactes et les maintenir à jour.</li>
          <li>Vérifier son adresse email lorsque le processus l&apos;exige.</li>
        </ul>
        <h4>3.2 Professionnels (Pros)</h4>
        <ul>
          <li>Disposer d&apos;un statut professionnel valide (ex. SIRET) et des autorisations requises.</li>
          <li>Transmettre les justificatifs demandés (Kbis/RNE, pièce d&apos;identité, justificatif de domicile, etc.).</li>
          <li>Attendre la validation administrative avant publication complète de l&apos;annonce.</li>
          <li>Respecter la réglementation applicable (consommation, assurance RC Pro, fiscalité, etc.).</li>
        </ul>
        <h4>3.3 ProMeet Partners</h4>
        <ul>
          <li>Soumettre un dossier complet (identité, extrait Kbis, attestation RC Pro, etc.).</li>
          <li>Ne pas accéder aux missions tant que le dossier n&apos;est pas approuvé et les spécialités validées.</li>
          <li>Ne postuler qu&apos;aux missions correspondant aux spécialités approuvées.</li>
        </ul>

        <h3>4. Fonctionnalités</h3>
        <ul>
          <li>Recherche de professionnels et réservation de créneaux.</li>
          <li>Notifications par email et, le cas échéant, SMS (prestataires tiers type Twilio).</li>
          <li>Gestion des indisponibilités, congés et rappels.</li>
          <li>Espace documents pour les abonnements éligibles.</li>
          <li>Module ProMeet Missions et réseau Partners selon les droits accordés.</li>
        </ul>

        <h3>5. Rendez-vous, annulation et retard</h3>
        <p>
          La réservation est confirmée après validation du créneau. Le Pro peut définir ses propres
          conditions d&apos;annulation. Le Client s&apos;engage à honorer le rendez-vous ou à
          prévenir le Pro en cas d&apos;empêchement.
        </p>
        <p>
          Pour les Clients consommateurs, le droit de rétractation de 14 jours (art. L221-18 C. consom.)
          peut ne pas s&apos;appliquer aux prestations de services à date déterminée (art. L221-28),
          selon la nature de la prestation. Les conditions précises sont celles affichées par le Pro.
        </p>

        <h3>6. Rôle et responsabilité de {siteName}</h3>
        <p>
          {siteName} met à disposition des outils de mise en relation et de gestion. Le Pro reste
          seul responsable de la qualité, de la conformité et de l&apos;exécution de ses prestations.
          {siteName} ne garantit pas la disponibilité permanente du service et ne saurait être tenue
          responsable des litiges entre utilisateurs, sous réserve des dispositions légales
          impératives.
        </p>

        <h3>7. Comportements interdits</h3>
        <ul>
          <li>Usurpation d&apos;identité, fraude, contournement des validations.</li>
          <li>Contenus illicites, diffamatoires ou portant atteinte aux droits de tiers.</li>
          <li>Extraction massive de données, attaques ou tentatives d&apos;intrusion.</li>
          <li>Utilisation des coordonnées obtenues via la Plateforme à des fins de prospection abusive.</li>
        </ul>

        <h3>8. Suspension et résiliation</h3>
        <p>
          {siteName} peut suspendre ou supprimer un compte en cas de manquement grave aux CGU,
          de fraude, de risque pour la sécurité des utilisateurs ou sur injonction légale.
        </p>

        <h3>9. Propriété intellectuelle</h3>
        <p>
          La Plateforme, sa marque, son interface et son code sont protégés. Chaque Pro conserve
          ses contenus et concède à {siteName} une licence non exclusive pour les afficher dans le
          cadre du service.
        </p>

        <h3>10. Droit applicable</h3>
        <p>
          Les CGU sont régies par le <b>droit français</b>. Litiges : {jurisdiction}, sans préjudice
          des règles protectrices du consommateur. Médiation de la consommation : le Client peut
          recourir à un médiateur agréé ; pour les litiges liés à une prestation, contacter
          d&apos;abord le Pro concerné.
        </p>
      </section>

      {/* ——— CGV ——— */}
      <section className="cgu-card" id="cgv">
        <h2>Conditions générales de vente (CGV) — Abonnements Pros</h2>
        <p>
          Les abonnements (Basic, Standard, Premium) donnent accès à des fonctionnalités
          supplémentaires (agenda avancé, documents, etc.). Les prix et durées sont affichés avant
          paiement.
        </p>
        <ul>
          <li>
            <b>Paiement :</b> traité par Stripe ; {siteName} n&apos;enregistre pas les données
            bancaires complètes.
          </li>
          <li>
            <b>Facturation :</b> selon l&apos;offre choisie ; renouvellement automatique sauf
            résiliation depuis l&apos;espace compte.
          </li>
          <li>
            <b>Rétractation :</b> pour les Clients consommateurs, délai légal applicable aux
            contrats à distance lorsque la loi le prévoit.
          </li>
          <li>
            <b>Résiliation :</b> possible à tout moment ; l&apos;accès reste actif jusqu&apos;à la fin
            de la période payée sauf mention contraire.
          </li>
        </ul>
        <p>
          Pour toute question commerciale : <a href={`mailto:${mail}`}>{mail}</a>.
        </p>
      </section>

      {/* ——— Missions ——— */}
      <section className="cgu-card" id="missions">
        <h2>ProMeet Missions & ProMeet Partners</h2>
        <p>
          <b>ProMeet Missions</b> permet la publication de missions B2B (automobile) et leur
          attribution à des Partners validés. Les missions créées par un Pro peuvent être soumises à
          validation administrative (prix, conformité).
        </p>
        <ul>
          <li>Seuls les Partners approuvés peuvent postuler ; l&apos;attribution est décidée par l&apos;administration.</li>
          <li>Les prix client / prestataire / marge peuvent être définis ou validés par l&apos;administration.</li>
          <li>Le Partner confirme sa présence selon le workflow prévu sur la Plateforme.</li>
        </ul>
        <p>
          {siteName} n&apos;est pas employeur des Partners et n&apos;intervient pas dans la relation
          commerciale entre le donneur d&apos;ordre et le prestataire, hors outils de mise en relation.
        </p>
      </section>

      {/* ——— RGPD ——— */}
      <section className="cgu-card" id="confidentialite">
        <h2>Politique de confidentialité (RGPD)</h2>
        <p>
          <b>Responsable de traitement :</b> {company.legalForm} {siteName}, {company.address}.
          Contact vie privée : <a href={`mailto:${company.dpoEmail || mail}`}>{company.dpoEmail || mail}</a>.
        </p>

        <h3>Données collectées</h3>
        <ul>
          <li>Identité, coordonnées, SIRET et justificatifs (Pros / Partners).</li>
          <li>Données de rendez-vous, missions, messages et documents métier.</li>
          <li>Données de connexion, logs techniques et cookies (voir section Cookies).</li>
          <li>Données de paiement traitées par Stripe (données bancaires non stockées par {siteName}).</li>
        </ul>

        <h3>Finalités et bases légales</h3>
        <ul>
          <li>Création et gestion des comptes (exécution du contrat).</li>
          <li>Réservations, notifications, support (exécution du contrat / intérêt légitime).</li>
          <li>Vérification des Pros et Partners (intérêt légitime / obligation légale).</li>
          <li>Facturation et abonnements (exécution du contrat).</li>
          <li>Sécurité, lutte contre la fraude (intérêt légitime).</li>
          <li>Prospection : uniquement avec consentement lorsque requis.</li>
        </ul>

        <h3>Durées de conservation</h3>
        <p>
          Les données sont conservées pendant la durée du compte, puis archivées selon les
          obligations légales (comptabilité, preuve, litiges). Les justificatifs de vérification
          sont conservés pour la durée nécessaire à la gestion du dossier et aux obligations
          applicables.
        </p>

        <h3>Vos droits</h3>
        <p>
          Vous disposez des droits d&apos;accès, rectification, effacement, limitation, opposition,
          portabilité et du droit de retirer votre consentement. Exercice :{" "}
          <a href={`mailto:${mail}`}>{mail}</a>. Réclamation : CNIL —{" "}
          <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
            www.cnil.fr
          </a>
          .
        </p>

        <h3>Sous-traitants</h3>
        <p>
          Hébergement (OVH), emails (Resend ou SMTP configuré), SMS (Twilio le cas échéant),
          paiements (Stripe), cartographie (Google Maps si activé). Des contrats conformes à
          l&apos;article 28 du RGPD encadrent ces traitements lorsque requis.
        </p>

        <h3>Transferts hors UE</h3>
        <p>
          Certains prestataires peuvent traiter des données hors Union européenne avec des garanties
          appropriées (clauses contractuelles types, décisions d&apos;adéquation). Informations
          disponibles sur demande à <a href={`mailto:${mail}`}>{mail}</a>.
        </p>
      </section>

      {/* ——— Cookies ——— */}
      <section className="cgu-card" id="cookies">
        <h2>Politique cookies</h2>
        <p>
          Un cookie est un petit fichier déposé sur votre terminal lors de la visite du site ou de
          l&apos;utilisation de l&apos;application.
        </p>
        <h3>Cookies strictement nécessaires</h3>
        <p>
          Indispensables au fonctionnement : session, authentification (jeton), préférences
          essentielles. Ils ne nécessitent pas de consentement préalable.
        </p>
        <h3>Cookies de mesure et marketing</h3>
        <p>
          S&apos;ils sont déployés, ils ne sont activés qu&apos;après votre consentement via le
          bandeau cookies. Vous pouvez retirer votre consentement à tout moment en effaçant les
          cookies ou via les paramètres du navigateur.
        </p>
        <h3>Gestion</h3>
        <p>
          Paramétrez votre navigateur (Chrome, Safari, Firefox, etc.) pour refuser ou supprimer
          les cookies. Le refus des cookies essentiels peut dégrader l&apos;expérience (connexion,
          réservation).
        </p>
      </section>

      {/* ——— Sécurité ——— */}
      <section className="cgu-card" id="securite">
        <h2>Sécurité</h2>
        <p>
          {siteName} met en œuvre des mesures techniques et organisationnelles adaptées : connexion
          sécurisée (HTTPS en production), mots de passe hashés, authentification par jeton,
          limitation du débit sur les routes sensibles, vérification des pièces jointes, contrôle
          d&apos;accès aux documents et validation manuelle des comptes Pros et Partners.
        </p>
        <p>
          Vous êtes responsable de la confidentialité de vos identifiants. En cas de suspicion
          d&apos;usage frauduleux, contactez immédiatement{" "}
          <a href={`mailto:${mail}`}>{mail}</a>
          {tel ? (
            <>
              {" "}
              ou <a href={`tel:${telHref}`}>{tel}</a>
            </>
          ) : null}
          .
        </p>
      </section>

      <footer className="cgu-foot">
        <p>
          Document mis à jour le {effectiveDate}. Pour toute question :{" "}
          <a href={`mailto:${mail}`}>{mail}</a>.
        </p>
        <small>
          Ce document constitue les conditions contractuelles de la Plateforme. Pour un conseil
          juridique personnalisé, consultez un professionnel du droit.
        </small>
      </footer>
    </div>
  );
}
